import { useMemo } from 'react';
import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { concatPagination } from '@apollo/client/utilities';
import merge from 'deepmerge';
import { setContext } from '@apollo/client/link/context';
import { getFromLocalStorage } from '../utils';
import { LocalStorageKeys } from '../enums/local-storage-keys';

let apolloClient: ApolloClient<any>;

function createApolloClient(gateway: string): ApolloClient<any> {
  const httpLink = createHttpLink({
    uri: gateway, // Server URL (must be absolute)
  });

  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists

    const token = getFromLocalStorage(
      LocalStorageKeys.AUTH_TOKEN,
      ''
    ) as string;

    return token && token.length ? { headers: { ...headers, token } } : headers;
  });

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: authLink.concat(httpLink),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            allPosts: concatPagination(),
          },
        },
      },
    }),
  });
}

export function initializeApollo(
  initialState: any = null,
  gateway: string
): any {
  const _apolloClient = apolloClient ?? createApolloClient(gateway);

  // Get existing cache, loaded during client side data fetching
  const existingCache = _apolloClient.extract();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState && existingCache) {
    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache);

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function useApollo(initialState: any, gateway: string): any {
  const store = useMemo(
    () => initializeApollo(initialState, gateway),
    [initialState]
  );
  return store;
}

export * from './effects';
export * from './redux';
export * from './axios';
